import {Heading, Text} from '@albert-io/atomic';
import React from 'react';

import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import MarkdownRenderer from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {criteriaFromRubric, totalPoints} from 'lib/rubric/criteria';

interface RubricPreviewProps {
  /**
   * The question's rubric, as returned by `question.getRubric()`: an Immutable Map
   * in app code (has `.toJS()`), a plain object in tests, or null/undefined when the
   * FRQ has no rubric. Kept as `unknown` so this component stays decoupled from the
   * two different question models (authoring vs. QuestionModelV3) that mount it.
   */
  rubric: unknown;
  /**
   * Rendered in place of the rubric when the flag is off or the FRQ has no criteria
   * rubric. Lets a caller swap the rubric in for another node (e.g. the question's
   * normal Explanation) so it's never silently dropped.
   */
  fallback?: React.ReactNode;
}

/** Convert a rubric value (Immutable Map or already-plain) to the plain shape the helper reads. */
function toPlainRubric(rubric: unknown): {type?: unknown; criteria?: unknown} | null {
  if (!rubric || typeof rubric !== 'object') {
    return null;
  }
  const maybeImmutable = rubric as {toJS?: () => unknown};
  const plain = typeof maybeImmutable.toJS === 'function' ? maybeImmutable.toJS() : rubric;
  return plain as {type?: unknown; criteria?: unknown};
}

/**
 * Whether `RubricPreview` would render the given rubric: the
 * `frq_rubric_authoring_dennis` flag is on AND the rubric is a criteria rubric.
 * Exported so mounts can coordinate surrounding content (e.g. hide the standard
 * Explanation) with the exact same rule the component applies internally.
 */
export function useRubricPreviewVisible(rubric: unknown): boolean {
  const {isFeatureEnabled} = useIsFeatureFlagEnabled('frq_rubric_authoring_dennis');
  return isFeatureEnabled && criteriaFromRubric(toPlainRubric(rubric)).length > 0;
}

/**
 * Read-only render of a free-response question's structured "criteria" rubric, shown
 * in the question preview (Dennis editor) and the post-submission footer. When the
 * `frq_rubric_authoring_dennis` flag is on AND the question has a criteria rubric it
 * renders the rubric; otherwise it renders `fallback` (or nothing), so a caller can
 * swap the rubric in for the question's normal Explanation. Safe no-op for non-flagged
 * users (including students). Editing stays in the authoring `RubricEditor`; the layout
 * mirrors that editor (total-points pill + a card per criterion) so authors see the
 * same rubric shape they edit.
 */
const RubricPreview = ({rubric, fallback = null}: RubricPreviewProps) => {
  const visible = useRubricPreviewVisible(rubric);
  const criteria = criteriaFromRubric(toPlainRubric(rubric));

  if (!visible) {
    return <>{fallback}</>;
  }

  return (
    <section className='mt-x4 mb-x4' aria-label='Rubric'>
      <div className='flex items-baseline justify-between gap-x2'>
        <Heading as='h2' size='s' uppercase className='my-x0'>
          Rubric
        </Heading>
        <div className='inline-flex shrink-0 items-center gap-x1 rounded-full bg-background-brand-subtle px-x2 py-x0-half whitespace-nowrap'>
          <Text as='span' size='s' color='secondary'>
            Total possible points
          </Text>
          <Text as='span' bold>
            {totalPoints(criteria)}
          </Text>
        </div>
      </div>

      <ol className='m-x0 mt-x2 flex list-none flex-col gap-x2 p-x0'>
        {criteria.map((criterion) => (
          <li key={criterion.id}>
            <div className='flex items-start justify-between gap-x2 rounded-x1 border border-solid border-border-primary background-primary p-x2'>
              <div className='flex flex-col gap-x0-half'>
                <Text bold>{criterion.title || 'Untitled criterion'}</Text>
                {criterion.description ? (
                  <MarkdownRenderer
                    text={criterion.description}
                    className='text-s text-content-secondary'
                  />
                ) : null}
              </div>
              <Text as='span' color='secondary' className='whitespace-nowrap'>
                {`${criterion.maxScore} ${criterion.maxScore === 1 ? 'pt' : 'pts'}`}
              </Text>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
};

export default RubricPreview;
